import React, {useCallback} from 'react';
import {
	useTimelineAccessKeysFindQuery,
	useTimelineAccessKeyGenerateMutation,
	useTimelineAccessKeyDeleteMutation,
} from '../services/timelineAccessKeys';
import {sharedViewPath} from '../common/sharedView';
import ShareLinkDialog from './ShareLinkDialog';

export type ShareTimelineDialogProps = {
	open: boolean;
	onClose: () => void;
	timelineId: string;
	timelineTitle?: string;
};

const ShareTimelineDialog = ({open, onClose, timelineId, timelineTitle}: ShareTimelineDialogProps) => {
	const {data: keys, isLoading, isFetching, refetch} = useTimelineAccessKeysFindQuery({timelineId}, {
		skip: !open || !timelineId,
	});
	const [generateKey, {isLoading: isGenerating}] = useTimelineAccessKeyGenerateMutation();
	const [deleteKey, {isLoading: isDeleting}] = useTimelineAccessKeyDeleteMutation();

	const secretKey = keys?.[0]?.secretKey;
	const shareLink = secretKey
		? new URL(sharedViewPath(secretKey, `/timeline/${timelineId}`), window.location.origin).toString()
		: '';

	const handleGenerate = useCallback(async () => {
		await generateKey({timelineId}).unwrap();
		refetch();
	}, [generateKey, timelineId, refetch]);

	const handleRevoke = useCallback(async () => {
		const id = keys?.[0]?.id;
		if (!id) {
			return;
		}
		await deleteKey({shareLinkId: id}).unwrap();
		refetch();
	}, [deleteKey, keys, refetch]);

	return (
		<ShareLinkDialog
			open={open}
			onClose={onClose}
			title={`Share${timelineTitle ? `: ${timelineTitle}` : ''}`}
			description='Anyone with the link can view this timeline and the entries plotted on it in read-only mode.'
			privateHint='This timeline is private. Click here to generate a secret link.'
			shareLink={shareLink}
			isLoading={isLoading}
			isGenerating={isGenerating}
			isDeleting={isDeleting}
			busy={isLoading || isFetching || isGenerating || isDeleting}
			onGenerate={handleGenerate}
			onRevoke={handleRevoke}
		/>
	);
};

export default ShareTimelineDialog;
